import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Volume2, Mic, ChevronRight, RotateCcw, Trophy, MicOff } from 'lucide-react';
import apiClient from '../../helpers/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PronounceItem {
  id: number;
  front: string;
  back: string;
  phonetic?: string;
  type: 'vocab' | 'sentence';
}

interface Props {
  items: PronounceItem[];
  onGameComplete?: (pct: number) => void;
  /** Item IDs already passed in a previous session — restored from server */
  initialPassedIds?: number[];
  /** Called immediately each time a word is passed (score >= 70) */
  onItemPassed?: (itemId: number, passedIds: number[]) => void;
}

// ─── Sound effects (Web Audio API — no external files needed) ───────────────────

function playCorrectSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    // Rising arpeggio: C5 → E5 → G5→ C6
    const notes = [523.25, 659.25, 783.99, 1046.5];
    const duration = 0.12;
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * duration;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.35, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
      osc.start(start);
      osc.stop(start + duration);
    });
    // Close context after sounds finish
    setTimeout(() => ctx.close(), (notes.length * duration + 0.2) * 1000);
  } catch { /* ignore if AudioContext not available */ }
}

function playWrongSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    // Descending whomp: two short notes going down
    const notes = [280, 200];
    const duration = 0.18;
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + i * duration);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.6, ctx.currentTime + i * duration + duration);
      gain.gain.setValueAtTime(0, ctx.currentTime + i * duration);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + i * duration + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * duration + duration);
      osc.start(ctx.currentTime + i * duration);
      osc.stop(ctx.currentTime + i * duration + duration);
    });
    setTimeout(() => ctx.close(), (notes.length * duration + 0.2) * 1000);
  } catch { /* ignore */ }
}

// ─── Scoring helpers ──────────────────────────────────────────────────────────

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[][] = [];
  for (let i = 0; i <= m; i++) {
    dp[i] = new Array(n + 1).fill(0);
    dp[i][0] = i;
  }
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9\s']/g, '').trim().replace(/\s+/g, ' ');
}

function computeScore(expected: string, transcript: string): number {
  const exp = normalize(expected);
  const got = normalize(transcript);
  if (!got) return 0;
  if (exp === got) return 100;

  const expWords = exp.split(' ').filter(Boolean);
  const gotWords = got.split(' ').filter(Boolean);

  if (expWords.length <= 1) {
    // Single word: character-level Levenshtein similarity
    const bestGot = gotWords[0] || got;
    const dist = levenshtein(exp, bestGot);
    const maxLen = Math.max(exp.length, bestGot.length, 1);
    return Math.max(0, Math.round((1 - dist / maxLen) * 100));
  } else {
    // Sentence: word-level overlap with 1-char fuzzy tolerance
    const pool = [...gotWords];
    let matched = 0;
    for (const w of expWords) {
      const idx = pool.findIndex(g => g === w || (w.length > 2 && levenshtein(g, w) <= 1));
      if (idx !== -1) { matched++; pool.splice(idx, 1); }
    }
    return Math.round((matched / expWords.length) * 100);
  }
}

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.7; }
  70%  { transform: scale(1.8); opacity: 0; }
  100% { transform: scale(1.8); opacity: 0; }
`;

const pulseMic = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.08); }
`;

const growBar = keyframes`
  from { width: 0; }
`;

const popIn = keyframes`
  0%   { transform: scale(0.6); opacity: 0; }
  70%  { transform: scale(1.1); }
  100% { transform: scale(1);   opacity: 1; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  animation: ${fadeUp} 0.35s ease;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 12px;
  flex-wrap: wrap;
`;

const ProgressLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #667eea;
`;

const ProgressTrack = styled.div`
  flex: 1;
  min-width: 120px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number; $passed: boolean }>`
  height: 100%;
  width: ${p => p.$pct}%;
  background: ${p => p.$passed
    ? 'linear-gradient(90deg, #22c55e, #16a34a)'
    : 'linear-gradient(90deg, #667eea, #764ba2)'};
  border-radius: 8px;
  transition: width 0.4s ease;
`;

const PassedCount = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #22c55e;
  white-space: nowrap;
`;

const ItemCard = styled.div`
  background: linear-gradient(135deg, #f8f9ff 0%, #fff 100%);
  border: 2px solid #e0e7ff;
  border-radius: 16px;
  padding: 14px 20px 18px;
  margin-bottom: 16px;
  box-shadow: 0 4px 24px rgba(102, 126, 234, 0.08);
  position: relative;
  overflow: hidden;
  animation: ${fadeUp} 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: -40px; right: -40px;
    width: 120px; height: 120px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(102,126,234,0.07), transparent 70%);
  }
`;

const ItemTypeBadge = styled.span<{ $type: 'vocab' | 'sentence' }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 50px;
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 10px;
  ${p => p.$type === 'vocab'
    ? 'background: #ede9fe; color: #7c3aed;'
    : 'background: #fef3c7; color: #d97706;'}
`;

/* centered block for word / phonetic / meaning */
const ItemCenter = styled.div`
  text-align: center;
`;

/* word + meaning on same line, centered */
const ItemWordRow = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const ItemWord = styled.div`
  font-size: 34px;
  font-weight: 800;
  color: #1a1a2e;
  letter-spacing: -0.5px;
  line-height: 1.2;
  margin-bottom: 4px;

  @media (max-width: 480px) {
    font-size: 26px;
  }
`;

const ItemPhonetic = styled.div`
  font-size: 18px;
  color: #7c3aed;
  font-style: italic;
  font-weight: 600;
  margin-bottom: 4px;
`;

const ItemMeaning = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const TTSBtn = styled.button<{ $speaking?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 20px;
  border-radius: 50px;
  border: none;
  background: ${p => p.$speaking
    ? 'linear-gradient(135deg, #a78bfa, #7c3aed)'
    : 'linear-gradient(135deg, #667eea, #764ba2)'};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.35);
  margin-bottom: 20px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(102, 126, 234, 0.45);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const RecordArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const MicWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PulseRing = styled.div`
  position: absolute;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.3);
  animation: ${pulseRing} 1.2s ease-out infinite;
`;

const RecordBtn = styled.button<{ $listening: boolean }>`
  position: relative;
  z-index: 1;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: none;
  background: ${p => p.$listening
    ? 'linear-gradient(135deg, #ef4444, #dc2626)'
    : 'linear-gradient(135deg, #667eea, #764ba2)'};
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${p => p.$listening
    ? '0 8px 24px rgba(239, 68, 68, 0.5)'
    : '0 8px 24px rgba(102, 126, 234, 0.45)'};
  transition: background 0.25s, box-shadow 0.25s;
  animation: ${p => p.$listening ? pulseMic : 'none'} 0.8s ease infinite;

  &:hover:not(:disabled) {
    transform: scale(1.05);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RecordLabel = styled.div<{ $listening: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$listening ? '#ef4444' : '#667eea'};
`;

// ─── Result footer (inside card) ────────────────────────────────────────────

const ItemFooter = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1.5px solid #e0e7ff;
  animation: ${fadeUp} 0.25s ease;
`;

const TranscriptRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const TranscriptLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  white-space: nowrap;
  padding-top: 2px;
`;

const TranscriptText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  font-style: italic;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const ScoreTrack = styled.div`
  flex: 1;
  height: 12px;
  background: #f1f5f9;
  border-radius: 12px;
  overflow: hidden;
`;

const ScoreFill = styled.div<{ $score: number }>`
  height: 100%;
  width: ${p => p.$score}%;
  background: ${p => p.$score >= 70
    ? 'linear-gradient(90deg, #22c55e, #16a34a)'
    : p.$score >= 40
    ? 'linear-gradient(90deg, #f59e0b, #d97706)'
    : 'linear-gradient(90deg, #ef4444, #dc2626)'};
  border-radius: 12px;
  animation: ${growBar} 0.7s ease forwards;
`;

const ScoreNum = styled.span<{ $score: number }>`
  font-size: 17px;
  font-weight: 800;
  min-width: 46px;
  text-align: right;
  color: ${p => p.$score >= 70 ? '#16a34a' : p.$score >= 40 ? '#d97706' : '#dc2626'};
`;

const ResultBadge = styled.div<{ $passed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 800;
  margin-bottom: 16px;
  animation: ${popIn} 0.4s ease;
  ${p => p.$passed
    ? 'background: #f0fdf4; color: #15803d; border: 2px solid #86efac;'
    : 'background: #fff5f5; color: #dc2626; border: 2px solid #fca5a5;'}
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const RetryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  border-radius: 50px;
  border: 2px solid #e0e7ff;
  background: #fff;
  color: #667eea;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #eef2ff; border-color: #a5b4fc; }
`;

const NextBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  border-radius: 50px;
  border: none;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(34, 197, 94, 0.35);
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(34, 197, 94, 0.45); }
`;

// ─── Unsupported / Finish screens ────────────────────────────────────────────

const CenterBox = styled.div`
  text-align: center;
  padding: 48px 24px;
  animation: ${fadeUp} 0.4s ease;
`;

const BigEmoji = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
`;

const CenterTitle = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 8px;
`;

const CenterSub = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 50px;
  border: none;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(102, 126, 234, 0.45); }
`;

const StatRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const StatBox = styled.div<{ $color: string }>`
  background: ${p => p.$color};
  border-radius: 16px;
  padding: 16px 24px;
  min-width: 100px;

  .num {
    font-size: 32px;
    font-weight: 900;
    color: #1a1a2e;
    line-height: 1;
  }
  .lbl {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    margin-top: 4px;
  }
`;

// ─── Main Component ───────────────────────────────────────────────────────────

const ReviewPronounceGame: React.FC<Props> = ({ items, onGameComplete, initialPassedIds = [], onItemPassed }) => {
  // Sort items ONCE on mount: unfinished words first, already-passed ones at the end.
  // IMPORTANT: must use useState (not useMemo) so the order is FIXED for the entire session.
  // useMemo with [items] dep would re-sort every time parent re-renders (because
  // items.filter() creates a new array reference each render), causing the card to jump
  // to a different word right after a word is marked as passed.
  const [sortedItems] = useState<PronounceItem[]>(() => {
    const passedSet = new Set(initialPassedIds);
    const unfinished = items.filter(it => !passedSet.has(it.id));
    const finished_ = items.filter(it => passedSet.has(it.id));
    return [...unfinished, ...finished_];
  });

  const [currentIdx, setCurrentIdx] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'listening' | 'result'>('idle');
  const [score, setScore] = useState(0);
  const [transcript, setTranscript] = useState('');
  // Initialize passedIds from server-persisted history
  const [passedIds, setPassedIds] = useState<Set<number>>(() => new Set(initialPassedIds));
  const [finished, setFinished] = useState(false);
  const [loadingTTS, setLoadingTTS] = useState(false);
  const recognitionRef = useRef<any>(null);
  const isPlayingTTSRef = useRef(false); // synchronous guard — state update is async and too slow
  const autoPlayedIdxRef = useRef<number>(-1); // track which idx we already auto-played
  // Flag: true when WE stopped the recognition intentionally (double-click, navigating away, etc.)
  // Prevents onend from misidentifying a manual stop as a Zalo/WebView silent-fail.
  const wasManuallyStoppedRef = useRef(false);

  const SpeechRecognitionAPI =
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  const isSupported = !!SpeechRecognitionAPI;

  // ── WebView / in-app browser detection ──────────────────────────────────────
  // Zalo, Facebook, Instagram WebViews often fake or partially support SpeechRecognition
  // but silently fail. Detect them by user agent.
  const isInAppBrowser = (() => {
    const ua = navigator.userAgent || '';
    return /ZaloApp|ZALO|Instagram|FBAN|FBAV|FB_IAB|TwitterAndroid|Line\/|MicroMessenger/i.test(ua)
      || (ua.includes('Mobile') && ua.includes('Safari') && !ua.includes('CriOS') && !ua.includes('FxiOS') && /wv|WebView/.test(ua));
  })();

  // Flag set when recording fires onend instantly (< 400ms) without any result — Zalo behaviour
  const [silentFail, setSilentFail] = useState(false);
  const showBrowserWarning = !isSupported || isInAppBrowser || silentFail;

  const currentItem = sortedItems[currentIdx];

  // ── TTS: same pattern as ReviewDetailPage (API + SpeechSynthesis fallback) ──
  const getFullAudioUrl = (audioUrl: string) => {
    if (!audioUrl) return '';
    if (audioUrl.startsWith('http://') || audioUrl.startsWith('https://')) return audioUrl;
    const base = process.env.REACT_APP_API_URL || '';
    return `${base}${audioUrl.startsWith('/') ? '' : '/'}${audioUrl}`;
  };

  const playTTS = useCallback(async (text: string) => {
    if (!text) return;
    if (isPlayingTTSRef.current) return; // synchronous re-entrancy guard
    isPlayingTTSRef.current = true;
    setLoadingTTS(true);
    try {
      const response = await apiClient.get(`/api/flashcards/tts/${encodeURIComponent(text)}`);
      if (response.data?.success && response.data?.audio_url) {
        const audio = new Audio(getFullAudioUrl(response.data.audio_url));
        await audio.play();
        return;
      }
    } catch {
      // fall through to browser TTS
    } finally {
      setLoadingTTS(false);
      isPlayingTTSRef.current = false;
    }
    if ('speechSynthesis' in window) {
      const utt = new SpeechSynthesisUtterance(text);
      utt.lang = 'en-US';
      utt.rate = 0.85;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utt);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-play TTS when item changes — guarded by ref to prevent double-fire
  // (React StrictMode mounts effects twice in development)
  useEffect(() => {
    if (!currentItem || finished) return;
    if (autoPlayedIdxRef.current === currentIdx) return; // already played this idx
    autoPlayedIdxRef.current = currentIdx;
    // Only auto-play if item not already passed (avoid replaying already-mastered words)
    if (!passedIds.has(currentItem.id)) {
      playTTS(currentItem.front);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, finished]);

  // ── Recording ────────────────────────────────────────────────────────────────

  const stopRecognition = useCallback(() => {
    if (recognitionRef.current) {
      wasManuallyStoppedRef.current = true; // mark as intentional stop before calling .stop()
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!isSupported || phase === 'listening' || !currentItem) return;
    // Cancel any ongoing TTS playback — if audio is playing when mic starts,
    // it feeds into the microphone and confuses the speech recognizer.
    window.speechSynthesis?.cancel();
    isPlayingTTSRef.current = false;
    stopRecognition();
    setPhase('listening');
    setTranscript('');
    setScore(0);

    const recognition = new SpeechRecognitionAPI();
    recognitionRef.current = recognition;
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 5;

    recognition.onresult = (event: any) => {
      // Pick the alternative with the best score
      let bestTranscript = '';
      let bestScore = -1;
      for (let i = 0; i < event.results[0].length; i++) {
        const t = event.results[0][i].transcript;
        const s = computeScore(currentItem.front, t);
        if (s > bestScore) { bestScore = s; bestTranscript = t; }
      }
      const finalScore = bestScore;
      setTranscript(bestTranscript || '(không nhận được)');
      setScore(finalScore);
      setPhase('result');
      // Play sound feedback immediately
      if (finalScore >= 70) {
        playCorrectSound();
      } else {
        playWrongSound();
      }
      if (finalScore >= 70) {
        setPassedIds(prev => {
          const updated = new Set([...prev, currentItem.id]);
          // Notify parent immediately so it can persist to server
          if (onItemPassed) {
            onItemPassed(currentItem.id, Array.from(updated));
          }
          return updated;
        });
      }
    };

    recognition.onerror = (e: any) => {
      const isSilent = e.error === 'no-speech';
      const msg = isSilent
        ? '(Không nghe thấy giọng nói — thử lại nhé!)'
        : `(Lỗi nhận dạng: ${e.error} — thử lại nhé!)`;
      setTranscript(msg);
      setScore(0);
      setPhase('result');
    };

    // If onend fires within 400ms without any result → browser silently rejected mic (Zalo/WebView)
    // EXCEPT when we manually stopped the recognition (double-click, navigation, etc.)
    const startedAt = Date.now();
    let gotResult = false;
    let gotError = false;
    wasManuallyStoppedRef.current = false; // reset flag for this new recording session
    const origOnResult = recognition.onresult;
    recognition.onresult = (event: any) => {
      gotResult = true;
      origOnResult(event);
    };
    const origOnError = recognition.onerror;
    recognition.onerror = (e: any) => {
      gotError = true;
      origOnError(e);
    };

    recognition.onend = () => {
      if (!gotResult && !wasManuallyStoppedRef.current && Date.now() - startedAt < 400) {
        // Silent instant-end with NO manual stop = WebView blocked mic silently
        setSilentFail(true);
        setPhase('idle');
        return;
      }
      if (!gotResult && !gotError && !wasManuallyStoppedRef.current) {
        // Recognition ended without any result or error (e.g. Chrome timed out silently).
        // Show a visible message so student knows to try again — don't silently go idle.
        setTranscript('(Không nhận được giọng nói — kiểm tra micro rồi thử lại nhé!)');
        setScore(0);
        setPhase('result');
        return;
      }
      // Only reset to idle if result hasn't been set yet (normal cases)
      setPhase(p => p === 'listening' ? 'idle' : p);
    };

    recognition.start();
  }, [isSupported, phase, currentItem, SpeechRecognitionAPI, stopRecognition]);

  // ── Navigation ────────────────────────────────────────────────────────────────

  const handleNext = useCallback(() => {
    stopRecognition();
    if (currentIdx >= sortedItems.length - 1) {
      setFinished(true);
      const pct = Math.round((passedIds.size / sortedItems.length) * 100);
      if (onGameComplete) onGameComplete(pct);
    } else {
      setCurrentIdx(i => i + 1);
      setPhase('idle');
      setScore(0);
      setTranscript('');
    }
  }, [currentIdx, sortedItems.length, passedIds, onGameComplete, stopRecognition]);

  const handleRetry = useCallback(() => {
    stopRecognition();
    setPhase('idle');
    setScore(0);
    setTranscript('');
  }, [stopRecognition]);

  const handleRestart = useCallback(() => {
    stopRecognition();
    setCurrentIdx(0);
    setPhase('idle');
    setScore(0);
    setTranscript('');
    setPassedIds(new Set(initialPassedIds));
    setFinished(false);
  }, [stopRecognition, initialPassedIds]);

  // Cleanup on unmount
  useEffect(() => () => { stopRecognition(); }, [stopRecognition]);

  // ── Guard: browser not supported ─────────────────────────────────────────────

  if (showBrowserWarning) {
    const currentUrl = window.location.href;
    return (
      <CenterBox>
        <BigEmoji>🌐</BigEmoji>
        <CenterTitle>
          {isInAppBrowser || silentFail
            ? 'Trình duyệt Zalo không hỗ trợ ghi âm'
            : 'Trình duyệt không hỗ trợ'}
        </CenterTitle>
        <CenterSub>
          {isInAppBrowser || silentFail ? (
            <>
              Trình duyệt trong ứng dụng Zalo chưa hỗ trợ tính năng ghi âm.<br />
              Vui lòng nhấn nút bên dưới để mở bằng <strong>Safari</strong> hoặc <strong>Chrome</strong>.
            </>
          ) : (
            <>
              Tính năng luyện phát âm cần quyền truy cập micro và Web Speech API.<br />
              Vui lòng dùng <strong>Google Chrome</strong> hoặc <strong>Safari</strong> để luyện tập.
            </>
          )}
        </CenterSub>
        {(isInAppBrowser || silentFail) && (
          <>
            <PrimaryBtn
              onClick={() => {
                // Try to open in external browser
                const intentUrl = `intent:${currentUrl}#Intent;end`;
                // For iOS: try to open directly
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                if (isIOS) {
                  // iOS: open link directly — user's default browser will handle it
                  window.location.href = currentUrl;
                } else {
                  // Android: use intent scheme to open in Chrome
                  window.open(`googlechrome://${currentUrl.replace(/^https?:\/\//, '')}`, '_blank');
                  setTimeout(() => {
                    // Fallback if Chrome intent didn't work
                    window.location.href = intentUrl;
                  }, 1500);
                }
              }}
            >
              🌍 Mở bằng Chrome / Safari
            </PrimaryBtn>
            <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
              Hoặc copy link và dán vào Chrome/Safari
            </div>
            <div
              style={{ marginTop: 6, padding: '8px 14px', background: '#f8fafc', borderRadius: 10, fontSize: 12, color: '#667eea', fontWeight: 600, wordBreak: 'break-all', cursor: 'pointer' }}
              onClick={() => navigator.clipboard?.writeText(currentUrl)}
            >
              📋 {currentUrl}
            </div>
          </>
        )}
      </CenterBox>
    );
  }

  if (items.length === 0) {
    return (
      <CenterBox>
        <BigEmoji>📭</BigEmoji>
        <CenterTitle>Không có từ nào để luyện</CenterTitle>
        <CenterSub>Bài ôn tập này chưa có từ vựng hay mẫu câu nào.</CenterSub>
      </CenterBox>
    );
  }

  // ── Finish screen ──────────────────────────────────────────────────────────

  if (finished) {
    const passed = passedIds.size;
    const total = sortedItems.length;
    const pct = Math.round((passed / total) * 100);
    return (
      <Wrapper>
        <CenterBox>
          <BigEmoji>{pct >= 80 ? '🏆' : pct >= 60 ? '🎉' : '💪'}</BigEmoji>
          <CenterTitle>
            {pct >= 80 ? 'Xuất sắc! Phát âm rất tốt!' : pct >= 60 ? 'Tốt lắm! Cố gắng thêm nhé!' : 'Cần luyện thêm nhiều hơn nha!'}
          </CenterTitle>
          <CenterSub>Bạn đã hoàn thành bài luyện phát âm.</CenterSub>
          <StatRow>
            <StatBox $color="#f0fdf4">
              <div className="num">✅ {passed}</div>
              <div className="lbl">Đạt</div>
            </StatBox>
            <StatBox $color="#fff5f5">
              <div className="num">❌ {total - passed}</div>
              <div className="lbl">Chưa đạt</div>
            </StatBox>
            <StatBox $color="#eef2ff">
              <div className="num">{pct}%</div>
              <div className="lbl">Tổng điểm</div>
            </StatBox>
          </StatRow>
          <PrimaryBtn onClick={handleRestart}>
            <RotateCcw size={16} /> Luyện lại từ đầu
          </PrimaryBtn>
        </CenterBox>
      </Wrapper>
    );
  }

  // ── Main game screen ───────────────────────────────────────────────────────

  // Banner shown when student has previous progress to resume
  const prevPassedCount = initialPassedIds.length;
  const unfinishedCount = sortedItems.filter(it => !passedIds.has(it.id)).length;

  const passed = passedIds.size;
  const progressPct = Math.round((passed / sortedItems.length) * 100);
  const isPassed = phase === 'result' && score >= 70;
  // If current item was already passed before (from prior session), highlight it
  const isAlreadyPassed = currentItem ? passedIds.has(currentItem.id) && phase !== 'result' : false;
  const isListening = phase === 'listening';

  return (
    <Wrapper>
      {/* Resume banner — shown when student has prior progress */}
      {prevPassedCount > 0 && currentIdx === 0 && phase === 'idle' && !finished && (
        <div style={{
          background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
          border: '2px solid #86efac',
          borderRadius: 14,
          padding: '10px 16px',
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          color: '#15803d',
          fontWeight: 600,
        }}>
          <span style={{ fontSize: 20 }}>📖</span>
          <div>
            Tiếp tục từ lần trước: <strong>{prevPassedCount}/{sortedItems.length}</strong> từ đã đạt.
            {unfinishedCount > 0
              ? <> Còn <strong>{unfinishedCount}</strong> từ chưa hoàn thành — đã ưu tiên lên đầu!</>
              : <> Tất cả từ đã hoàn thành — luyện lại để ôn nhé!</>}
          </div>
        </div>
      )}

      {/* Progress bar */}
      <ProgressHeader>
        <ProgressLabel>
          Từ {currentIdx + 1}/{sortedItems.length}
        </ProgressLabel>
        <ProgressTrack>
          <ProgressFill $pct={progressPct} $passed={true} />
        </ProgressTrack>
        <PassedCount>✅ {passed} đạt</PassedCount>
      </ProgressHeader>

      {/* Item card — with result footer when scored */}
      <ItemCard key={currentItem?.id} style={isAlreadyPassed ? { borderColor: '#86efac', background: 'linear-gradient(135deg, #f0fdf4, #fff)' } : undefined}>
        {/* Badge — left aligned */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <ItemTypeBadge $type={currentItem?.type || 'vocab'} style={{ marginBottom: 0 }}>
            {currentItem?.type === 'vocab' ? '📚 Từ vựng' : '💬 Mẫu câu'}
          </ItemTypeBadge>
          {isAlreadyPassed && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: 50 }}>✅ Đã hoàn thành</span>
          )}
        </div>
        {/* Word / phonetic / meaning — centered */}
        <ItemCenter>
          <ItemWordRow>
            <ItemWord>{currentItem?.front}</ItemWord>
            <ItemMeaning>— {currentItem?.back}</ItemMeaning>
          </ItemWordRow>
          {currentItem?.phonetic && (
            <ItemPhonetic>/{currentItem.phonetic.replace(/^\/|\/$/g, '')}/</ItemPhonetic>
          )}
        </ItemCenter>

        {/* Result footer — shows after scoring */}
        {phase === 'result' && (
          <ItemFooter>
            <TranscriptRow>
              <TranscriptLabel>Bạn nói:</TranscriptLabel>
              <TranscriptText>"{transcript}"</TranscriptText>
              <ScoreTrack style={{ flex: 1, minWidth: 60 }}>
                <ScoreFill $score={score} />
              </ScoreTrack>
              <ScoreNum $score={score}>{score}%</ScoreNum>
            </TranscriptRow>
            <ResultBadge $passed={isPassed}>
              {isPassed ? '✅ Đạt! Phát âm tốt lắm!' : score >= 40
                ? '🟡 Gần đúng rồi! Cố thêm chút nữa nhé!'
                : '❌ Chưa đạt — Hãy thử lại!'}
            </ResultBadge>
          </ItemFooter>
        )}
      </ItemCard>

      {/* TTS button */}
      <div style={{ textAlign: 'center' }}>
        <TTSBtn
          onClick={() => playTTS(currentItem?.front || '')}
          disabled={loadingTTS || isListening}
          $speaking={loadingTTS}
        >
          <Volume2 size={15} />
          {loadingTTS ? 'Đang phát...' : 'Nghe mẫu phát âm'}
        </TTSBtn>
      </div>

      {/* Action area: mic when idle/listening, action buttons when result */}
      {phase === 'result' ? (
        <ActionRow>
          <RetryBtn onClick={handleRetry}>
            <RotateCcw size={13} /> Nói lại
          </RetryBtn>
          <NextBtn onClick={handleNext}>
            {isPassed
              ? (currentIdx < items.length - 1 ? 'Từ tiếp theo' : 'Hoàn thành')
              : (currentIdx < items.length - 1 ? 'Bỏ qua' : 'Kết thúc')}
            <ChevronRight size={15} />
          </NextBtn>
        </ActionRow>
      ) : (
        <RecordArea>
          <MicWrapper>
            {isListening && <PulseRing />}
            <RecordBtn
              $listening={isListening}
              onClick={isListening ? stopRecognition : startRecording}
              title={isListening ? 'Dừng ghi âm' : 'Bắt đầu ghi âm'}
            >
              {isListening ? <MicOff size={28} /> : <Mic size={28} />}
            </RecordBtn>
          </MicWrapper>
          <RecordLabel $listening={isListening}>
            {isListening ? '🔴 Đang nghe... Hãy nói to!' : '🎤 Bấm để bắt đầu nói'}
          </RecordLabel>
        </RecordArea>
      )}
    </Wrapper>
  );
};

export default ReviewPronounceGame;
